import axios from 'axios';
import {
  getDeveloperAccessToken,
  isDeveloperOAuthEnabled,
  refreshDeveloperAccessToken,
} from 'src/services/developerOAuth';

const xsrfCookieName = process.env.REACT_APP_XSRF_COOKIE || 'autoappy_app_xsrf';
const useDeveloperOAuth = isDeveloperOAuthEnabled();

axios.defaults.withCredentials = !useDeveloperOAuth;
axios.defaults.withXSRFToken = !useDeveloperOAuth;
axios.defaults.xsrfCookieName = xsrfCookieName;
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

// Create an instance of axios
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`, // Set default base URL
  withCredentials: !useDeveloperOAuth,
  withXSRFToken: !useDeveloperOAuth,
  xsrfCookieName,
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

export const ensureCsrfCookie = () => useDeveloperOAuth
  ? Promise.resolve()
  : axiosInstance.get('/sanctum/csrf-cookie', { __autoAppySkipCsrfRetry: true });

let sessionRefresh;
export const refreshAppSession = () => {
  if (!sessionRefresh) {
    sessionRefresh = (async () => {
      await ensureCsrfCookie();
      return axiosInstance.get('/api/auth/me');
    })().finally(() => { sessionRefresh = undefined; });
  }
  return sessionRefresh;
};

axiosInstance.interceptors.request.use(async (request) => {
  const token = await getDeveloperAccessToken();
  if (token) request.headers.Authorization = `Bearer ${token}`;

  const workspaceId = localStorage.getItem('autoappy_current_workspace_id');
  if (workspaceId && !request.headers['X-Workspace-ID']) {
    request.headers['X-Workspace-ID'] = workspaceId;
  }

  return request;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error.config;
    // Orchestration checks the current identity before any write. A 401 can
    // therefore safely renew the cookie session and retry that rejected request
    // once, preserving its event ID / expected revision. Never retry an uncertain
    // network/server failure or an explicit permission denial.
    if (!useDeveloperOAuth && error.response?.status === 401 && request
      && request.url?.startsWith('/api/orchestration/') && !request.__autoAppySessionRetried) {
      request.__autoAppySessionRetried = true;
      try {
        await refreshAppSession();
        return axiosInstance(request);
      } catch {
        return Promise.reject(error);
      }
    }
    if (useDeveloperOAuth && error.response?.status === 401 && request
      && !request.__autoAppyOAuthRetried) {
      request.__autoAppyOAuthRetried = true;
      try {
        const token = await refreshDeveloperAccessToken();
        if (token) {
          request.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(request);
        }
      } catch {
        // The AuthContext will start a fresh PKCE login when refresh fails.
      }
    }

    if (error.response?.status === 419 && request
      && !request.__autoAppyCsrfRetried && !request.__autoAppySkipCsrfRetry) {
      request.__autoAppyCsrfRetried = true;
      await ensureCsrfCookie();
      return axiosInstance(request);
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
